/*!

=========================================================
* Argon Dashboard React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Spinner,
  Table,
} from "reactstrap";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import compose from "recompose/compose";
import Chart from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  chartOptions,
  parseOptions,
  barChartOptions,
} from "variables/charts.jsx";
import ReactJson from "react-json-view";
import ReactBSAlert from "react-bootstrap-sweetalert";
import ImageGallery from "react-image-gallery";
import {isMobile} from 'react-device-detect';
import range from "lodash-es/range";
import groupBy from "lodash-es/groupBy";
import map from "lodash-es/map";
import forEach from "lodash-es/forEach";
import flatMap from "lodash-es/flatMap";
import countBy from "lodash-es/countBy";
import pickBy from "lodash-es/pickBy";
import flatten from "lodash-es/flatten";


import DocumentDeIdentificationHeader from "components/Headers/DocumentDeIdentificationHeader.jsx";
import { 
  crudGetOneById as crudGetOneByIdAction,
} from 'store/actions/dataActions';
import { 
  showNotification as showNotificationAction
} from 'store/actions/notificationActions';
import { downloadFile } from 'util/common';
import { 
  SAMPLE_FILE_BTN_COLORS,
  DEIDENTIFICATION_PREVIEW_IMAGE_INFO,
  DEIDENTIFICATION_PREVIEW_FILE_EXTENSION,
  DOCUMENT_DEIDENTIFICATION_SAMPLE_DOWNLOAD_PATH,
  DOCUMENT_DEIDENTIFICATION_SAMPLE_DOWNLOAD_NAME,
  DEIDENTIFICATION_ITEM_NAME,
} from 'util/constants';


class DocumentDeIdentification extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      previewImages: null,
    };
  }

  componentWillMount() {
    if (window.Chart) {
      parseOptions(Chart, chartOptions());
    }
  }

  onHidePreview = (event) => {
    this.setState({
      previewImages: null,
    });
  };

  onPreviewSampleFile = (previewNo) => (event) => {
    if( previewNo && previewNo > 0 ) {
      this.setState({
        previewImages: DEIDENTIFICATION_PREVIEW_IMAGE_INFO[previewNo-1]
      });
    }
  }

  onDeIdentificationSampleFile = (no) => (event) => {
    this.props.dispatchCrudGetOneById(this.props.menu+".SAMPLE", no);
  }

  // 샘플파일 다운로드
  onDownloadSampleFile = event => {
    event.stopPropagation();
    downloadFile(DOCUMENT_DEIDENTIFICATION_SAMPLE_DOWNLOAD_PATH, DOCUMENT_DEIDENTIFICATION_SAMPLE_DOWNLOAD_NAME, "isDownloadingSampleFile", this, this.props.dispatchShowNotification)
  }

  // 비식별화 결과 파일 다운로드버튼 클릭
  onDownloadResultFile = () => {
    const downloadUrl = this.props.item && this.props.item.downloadUrl;
    const filename = this.props.item && this.props.item.fileName;
    downloadFile(downloadUrl, filename, "isDownloadingResultFile", this, this.props.dispatchShowNotification)
  }

  getClassifiedPersonalInfo( results ) {
    let personalInfos = [];
    if( results ) {
      const personalInfoByType = groupBy(results, "infoType");
      // personalInfoByType sample
      // {
      //   "EMAIL_ADDRESS": [{
      //     "infoType": "EMAIL_ADDRESS", "content": ["example@wesome.co.kr"],
      //     "infoType": "EMAIL_ADDRESS", "content": ["example@wesome.co.kr", "example2@wesome.co.kr"],
      //   }],
      //   "KOREA_RRN": ...
      // }

      const cleanedPersonalInfoByType = pickBy(personalInfoByType, function(data, keyName) {
        return !keyName.startsWith("KOREA_PHONE_NUMBER");
      })
      const phoneGroupInfos = pickBy(personalInfoByType, function(data, keyName) {
        return keyName.startsWith("KOREA_PHONE_NUMBER");
      })
      const phoneInfos = flatten(Object.values(phoneGroupInfos));
      if( phoneInfos && phoneInfos.length > 0 ) {
        cleanedPersonalInfoByType.KOREA_PHONE_NUMBER = phoneInfos;
      }

      forEach(cleanedPersonalInfoByType, function(result, infoType) {
        const contents = flatMap( map(result, "content") );
        const countInfos = countBy(contents);

        personalInfos.push({
          type: DEIDENTIFICATION_ITEM_NAME[infoType],
          contents: countInfos,
          totalCnt: contents.length
        })
      });
    }

    return personalInfos;
  }

  getChartData( personalInfos ) {
    if( personalInfos && personalInfos.length > 0 ) {
      return {
        labels: personalInfos.map( personalInfo => personalInfo.type ),
        datasets: [
          {
            label: "개인정보 검출 갯수",
            data: personalInfos.map( personalInfo => personalInfo.totalCnt ),
          }
        ]
      }
    } else {
      return null;
    }
  }

  render() {
    const { menu, item } = this.props;
    const { isDownloadingResultFile, isDownloadingSampleFile, previewImages } = this.state;
    const sampleFileCnt = DEIDENTIFICATION_PREVIEW_IMAGE_INFO.length;
    const personalInfos = this.getClassifiedPersonalInfo(item && item.results);
    const chartData = this.getChartData(personalInfos);

    return (
      <>
        <DocumentDeIdentificationHeader menu={menu} form="DIForm" />
        { previewImages && previewImages.length > 0 && <ReactBSAlert
          custom
          title="Preview"
          onConfirm={this.onHidePreview}
          customButtons={
            <Button
              color="primary"
              onClick={this.onHidePreview}
            >
            닫기
            </Button>
          }
        >
          <ImageGallery 
            showThumbnails={false}
            infinite={false}
            showIndex={true}
            lazyLoad={true}
            showFullscreenButton={isMobile ? false : true}
            //showPlayButton={false}
            items={previewImages}
          />
        </ReactBSAlert> }

        <Container className=" mt--7" fluid>
          { item && ( 
            <>
              <Row>
                {/* Chart */}
                <Col className="mb-5 mb-xl-0" xl="4">
                  <Card className="shadow">
                    <CardHeader className="bg-transparent">
                      <Row className="align-items-center">
                        <div className="col">
                          <h3 className="mb-0">
                            항목별 비식별화 처리 갯수
                          </h3>
                        </div>
                        { item.downloadUrl && item.fileName ?  (
                          <div className="col text-right">
                            <Button
                              color="primary"
                              size="sm"
                              disabled={ isDownloadingResultFile ? true : false }
                              onClick={this.onDownloadResultFile}
                            >
                              <span className="btn-inner--icon">
                                { isDownloadingResultFile ? <Spinner size="sm" color="light" /> : null }
                              </span>
                              <span className="btn-inner--text">비식별화 파일 다운로드</span>
                            </Button>
                          </div>
                        ) : null }
                      </Row>
                    </CardHeader>
                    <CardBody>
                      { !chartData && (
                        <h5 className="text-center">비식별화된 데이타가 없습니다.</h5>
                      )}
                      { chartData && 
                        <div className="chart">
                          <Bar
                            data={chartData}
                            options={barChartOptions}
                          />
                        </div>
                      }
                    </CardBody>
                  </Card>
                </Col>
                {/* 표 */}
                <Col xl="8">
                  <Card className="shadow">
                    <CardHeader className="bg-transparent">
                      <Row className="align-items-center">
                        <div className="col">
                          <h3 className="mb-0">
                            데이타별 비식별화 처리 갯수
                          </h3>
                        </div>
                      </Row>
                    </CardHeader>
                    <CardBody>
                      <Table className="align-items-center table-flush" responsive>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">항목</th>
                            <th scope="col">데이타</th>
                            <th scope="col">검출 횟수</th>
                          </tr>
                        </thead>
                        <tbody>
                          { personalInfos.map( item => {
                            const contents = Object.keys(item.contents);
                            const contentCnt = contents.length;
                            return (
                              contents.map((content, idx) => (
                                <tr key={`${item.type}_${idx}`}>
                                  { idx === 0 && (
                                    <th scope="row" rowSpan={contentCnt}>{item.type}</th>
                                  )}
                                  <td>{content}</td>
                                  <td>{item.contents[content]}</td>
                                </tr>
                              ))
                            );
                          })}
                          { personalInfos.length === 0 && (
                            <tr>
                              <td colSpan="3" className="text-center">비식별화된 데이타가 없습니다.</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-5">
                <div className=" col">
                  <Card className=" shadow">
                    <CardHeader className="bg-transparent">
                      <Row className="align-items-center">
                        <div className="col">
                          <h6 className="text-uppercase ls-1 mb-1 gray-600">
                            Overview
                          </h6>
                          <h2 className=" mb-0">비식별화 처리 결과</h2>
                        </div>
                        { item.downloadUrl && item.fileName ?  (
                          <div className="col text-right">
                            <Button
                              color="primary"
                              disabled={ isDownloadingResultFile ? true : false }
                              onClick={this.onDownloadResultFile}
                            >
                              <span className="btn-inner--icon">
                                { isDownloadingResultFile ? <Spinner size="sm" color="light" /> : null }
                              </span>
                              <span className="btn-inner--text">비식별화 파일 다운로드</span>
                            </Button>
                          </div>
                        ) : null }
                      </Row>
                    </CardHeader>
                    <CardBody>
                      {/* 비식별화 처리 결과 */}
                      <ReactJson src={item} theme="monokai" />
                    </CardBody>
                  </Card>
                </div>
              </Row>
            </>
          )}

          <Row className={ item ? "mt-5" : ""}>
            <Col className="mb-5 mb-xl-0" xl="8">
              <Card className="shadow">
                <CardHeader className="bg-transparent">
                  <Row className="align-items-center">
                    <div className="col">
                      <h6 className="text-uppercase text-light ls-1 mb-1">
                        TEST
                      </h6>
                      <h2 className="mb-0">샘플파일로 테스트</h2>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="text-center">
                  { range(1, sampleFileCnt+1).map((no) => {
                    const color = SAMPLE_FILE_BTN_COLORS[no%(SAMPLE_FILE_BTN_COLORS.length)];
                    return (
                      <React.Fragment key={`sample${no}`}>
                        <Button
                          color={color}
                          className="mb-1"
                          onClick={this.onPreviewSampleFile(no)}
                        >
                          샘플파일{no}
                          { DEIDENTIFICATION_PREVIEW_FILE_EXTENSION[no-1] ? ` (${DEIDENTIFICATION_PREVIEW_FILE_EXTENSION[no-1]}) ` : " "}
                          미리보기
                        </Button>
                        <Button
                          outline
                          color={color}
                          className="mb-1"
                          onClick={this.onDeIdentificationSampleFile(no)}
                        >
                          샘플파일{no}
                          { DEIDENTIFICATION_PREVIEW_FILE_EXTENSION[no-1] ? ` (${DEIDENTIFICATION_PREVIEW_FILE_EXTENSION[no-1]}) ` : " "}
                          분석하기
                        </Button>
                        <hr className="mt-2 mb-2" />
                      </React.Fragment>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
            <Col xl="4">
              <Card className="shadow">
                <CardHeader className="bg-transparent">
                  <Row className="align-items-center">
                    <div className="col">
                      <h6 className="text-uppercase text-muted ls-1 mb-1">
                        DOWNLOAD
                      </h6>
                      <h2 className="mb-0">샘플파일 다운로드</h2>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody>
                  <Row className="align-items-center">
                    <div className="col text-center">
                      <Button
                        color="primary"
                        disabled={ isDownloadingSampleFile ? true : false }
                        onClick={this.onDownloadSampleFile}
                      >
                        <span className="btn-inner--icon">
                          { isDownloadingSampleFile ? <Spinner size="sm" color="light" /> : null }
                        </span>
                        <span className="btn-inner--text">템플릿을 다운받아 내용을 수정 후 사용해보세요</span>
                      </Button>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }
}

export default compose(
  connect(
    ( state, props ) => ({
      item: state.main.item,
    }), {
      dispatchCrudGetOneById: crudGetOneByIdAction,
      dispatchShowNotification: showNotificationAction,
    }
  ),
)(DocumentDeIdentification);

DocumentDeIdentification.propTypes = {
  menu: PropTypes.string.isRequired,
};